import { takeEvery, put, select, debounce, delay, take, fork, cancel } from 'redux-saga/effects'
import { SagaIterator, Task } from 'redux-saga'
import { navigatorPush, navigatorReplace } from 'onsenui-react-redux-navigator'

import * as a from './actions'
import * as s from './selectors'
import * as da from 'modules/database/actions'

function* handleChooseProduct(): SagaIterator {
	yield put(navigatorPush({
		navigator: 'maqasa',
		route: {
			component: 'ProductForm',
		},
	}))
}

function* handleNewProduct(action: ReturnType<typeof a.newProduct>): SagaIterator {
	if (action.payload.navigationReplace) {
		yield put(navigatorReplace({
			navigator: 'maqasa',
			route: {
				component: 'ProductForm',
			},
		}))
	} else {
		yield put(navigatorPush({
			navigator: 'maqasa',
			route: {
				component: 'ProductForm',
			},
		}))
	}
}

function* handleUpdateProduct(): SagaIterator {
	const product = (yield select(s.updateProductPayload)) as ReturnType<typeof s.updateProductPayload>
	if (product) {
		yield put(da.updateProduct(product))
		yield put(a.productSyncDispatched())
	}
}

/**
 * Debounce incoming downloadedProduct actions for the current product.
 *
 * After a local edit is uploaded, Firestore may send back multiple snapshots
 * (echo of our write, then possibly updates from other users). We hold incoming
 * updates for 2s and only apply the latest, so we don't clobber the UI with
 * intermediate states. If the user types during this window, dirty blocks it.
 */
function* watchDownloadedProduct(): SagaIterator {
	let pendingTask: Task | undefined

	while (true) {
		const action = (yield take([da.downloadedProduct, a.updateCurrentProductDetails])) as
			ReturnType<typeof da.downloadedProduct> | ReturnType<typeof a.updateCurrentProductDetails>

		if (action.type === a.updateCurrentProductDetails.type) {
			/* User is typing — cancel any pending remote update */
			if (pendingTask) {
				console.debug('PRODUCT: cancelling pending remote update due to local edit')
				yield cancel(pendingTask)
				pendingTask = undefined
			}
			continue
		}

		const downloadedAction = action as ReturnType<typeof da.downloadedProduct>
		const state = (yield select()) as { product: { currentProduct?: { productId: string }, projectId?: string } }
		const currentProduct = state.product.currentProduct

		/* Only debounce updates for the product we're currently editing */
		if (currentProduct && downloadedAction.payload.projectId === state.product.projectId && currentProduct.productId === downloadedAction.payload.product.productId) {
			/* Cancel any pending debounce and restart with the latest data */
			if (pendingTask) {
				yield cancel(pendingTask)
			}
			pendingTask = (yield fork(applyAfterDelay, downloadedAction.payload)) as Task
		}
	}
}

function* applyAfterDelay(payload: da.DownloadedProductPayload): SagaIterator {
	yield delay(2000)
	yield put(a.applyDownloadedProduct(payload))
}

/** The saga function for this module that is run from our root saga. */
export default function* productSagas(): SagaIterator {
	yield takeEvery(a.editProduct, handleChooseProduct)
	yield takeEvery(a.newProduct, handleNewProduct)
	yield debounce(1000, a.updateCurrentProductDetails, handleUpdateProduct)
	yield fork(watchDownloadedProduct)
}
